import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Save, X, Building2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { partnerApi } from '../lib/api/partnerApi';
import { CreatePartnerRequest, UpdatePartnerRequest, PartnerDto } from '../lib/api/types';

interface PartnerFormProps {
  partner?: PartnerDto; // If provided, we're editing; otherwise, creating
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const PartnerForm: React.FC<PartnerFormProps> = ({ partner, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEditing = !!partner;

  // Form state
  const [formData, setFormData] = useState({
    partnerName: partner?.partnerName || '',
    partnerCode: partner?.partnerCode || '',
    billerId: partner?.billerId || '',
    authToken: '',
    termId: '',
    certStr: '',
    webhookSecret: partner?.webhookSecret || '',
    description: partner?.description || '',
    isActive: partner?.isActive ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create partner mutation
  const createPartnerMutation = useMutation({
    mutationFn: partnerApi.createPartner,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success(`Partner "${data.partnerName}" created successfully`);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(`Failed to create partner: ${error.message}`);
    },
  });

  // Update partner mutation
  const updatePartnerMutation = useMutation({
    mutationFn: ({ partnerId, data }: { partnerId: string; data: UpdatePartnerRequest }) =>
      partnerApi.updatePartner(partnerId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success(`Partner "${data.partnerName}" updated successfully`);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update partner: ${error.message}`);
    },
  });

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.partnerName.trim()) {
      newErrors.partnerName = 'Partner name is required';
    }

    if (!formData.partnerCode.trim()) {
      newErrors.partnerCode = 'External Partner ID is required';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.partnerCode)) {
      newErrors.partnerCode = 'External Partner ID can only contain letters, numbers, hyphens, and underscores';
    }

    if (!formData.termId.trim()) {
      newErrors.termId = 'Terminal ID is required';
    }

    if (!formData.certStr.trim()) {
      newErrors.certStr = 'Certificate string is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (isEditing && partner) {
      // Update existing partner
      const updateData: UpdatePartnerRequest = {
        partnerName: formData.partnerName,
        billerId: formData.billerId || undefined,
        authToken: formData.authToken || undefined,
        termId: formData.termId,
        certStr: formData.certStr,
        webhookSecret: formData.webhookSecret || undefined,
        description: formData.description || undefined,
        isActive: formData.isActive,
      };
      updatePartnerMutation.mutate({ partnerId: partner.partnerId, data: updateData });
    } else {
      // Create new partner
      const createData: CreatePartnerRequest = {
        partnerName: formData.partnerName,
        partnerCode: formData.partnerCode,
        billerId: formData.billerId || undefined,
        authToken: formData.authToken || undefined,
        termId: formData.termId,
        certStr: formData.certStr,
        webhookSecret: formData.webhookSecret || undefined,
        description: formData.description || undefined,
        isActive: formData.isActive,
      };
      createPartnerMutation.mutate(createData);
    }
  };

  // Handle input changes
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isLoading = createPartnerMutation.isPending || updatePartnerMutation.isPending;

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          {isEditing ? `Edit Partner: ${partner.partnerName}` : 'Create New Partner'}
        </CardTitle>
        <CardDescription>
          {isEditing
            ? 'Update the partner information below'
            : 'Fill in the details to create a new partner organization'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Partner Name */}
          <div className="space-y-2">
            <Label htmlFor="partnerName">Partner Name *</Label>
            <Input
              id="partnerName"
              value={formData.partnerName}
              onChange={(e) => handleInputChange('partnerName', e.target.value)}
              placeholder="Enter partner name"
              className={errors.partnerName ? 'border-destructive' : ''}
            />
            {errors.partnerName && (
              <p className="text-sm text-destructive">{errors.partnerName}</p>
            )}
          </div>

          {/* External Partner ID */}
          <div className="space-y-2">
            <Label htmlFor="partnerCode">External Partner ID *</Label>
            <Input
              id="partnerCode"
              value={formData.partnerCode}
              onChange={(e) => handleInputChange('partnerCode', e.target.value.toUpperCase())}
              placeholder="PARTNER001"
              disabled={isEditing} // Can't change code when editing
              className={errors.partnerCode ? 'border-destructive' : ''}
            />
            {errors.partnerCode && (
              <p className="text-sm text-destructive">{errors.partnerCode}</p>
            )}
            {isEditing && (
              <p className="text-sm text-muted-foreground">
                External Partner ID cannot be changed after creation
              </p>
            )}
          </div>

          {/* Biller ID */}
          <div className="space-y-2">
            <Label htmlFor="billerId">Biller ID</Label>
            <Input
              id="billerId"
              value={formData.billerId}
              onChange={(e) => handleInputChange('billerId', e.target.value)}
              placeholder="Enter biller ID (optional)"
            />
          </div>

          {/* Auth Token */}
          <div className="space-y-2">
            <Label htmlFor="authToken">Authentication Token</Label>
            <Input
              id="authToken"
              type="password"
              value={formData.authToken}
              onChange={(e) => handleInputChange('authToken', e.target.value)}
              placeholder="Enter auth token (optional)"
            />
          </div>

          {/* Terminal ID */}
          <div className="space-y-2">
            <Label htmlFor="termId">Terminal ID *</Label>
            <Input
              id="termId"
              value={formData.termId}
              onChange={(e) => handleInputChange('termId', e.target.value)}
              placeholder="Enter terminal ID"
              className={errors.termId ? 'border-destructive' : ''}
            />
            {errors.termId && (
              <p className="text-sm text-destructive">{errors.termId}</p>
            )}
          </div>

          {/* Certificate String */}
          <div className="space-y-2">
            <Label htmlFor="certStr">Certificate String *</Label>
            <Input
              id="certStr"
              value={formData.certStr}
              onChange={(e) => handleInputChange('certStr', e.target.value)}
              placeholder="Enter certificate string"
              className={errors.certStr ? 'border-destructive' : ''}
            />
            {errors.certStr && (
              <p className="text-sm text-destructive">{errors.certStr}</p>
            )}
          </div>

          {/* Webhook Secret */}
          <div className="space-y-2">
            <Label htmlFor="webhookSecret">Webhook Secret</Label>
            <Input
              id="webhookSecret"
              value={formData.webhookSecret}
              onChange={(e) => handleInputChange('webhookSecret', e.target.value)}
              placeholder="Enter webhook secret for signature verification (optional)"
            />
            <p className="text-sm text-muted-foreground">
              Used to verify webhook signatures in incoming requests
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter partner description (optional)"
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="isActive">Active Partner</Label>
          </div>

          {/* Form Actions */}
          <div className="flex items-center gap-3 pt-4">
            <Button type="submit" disabled={isLoading}>
              <Save className="h-4 w-4 mr-2" />
              {isLoading
                ? (isEditing ? 'Updating...' : 'Creating...')
                : (isEditing ? 'Update Partner' : 'Create Partner')}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
